import { Carrier } from 'app/pages/ToursPage/types';
import { compareDesc, isToday, parseISO, subDays } from 'date-fns';
import { PartOrder } from 'app/models/PartOrder';
import { Delivery } from 'app/models/Delivery';
import { Driver } from 'app/pages/DriverPage/types';
import { createDestination, Destination } from 'app/models/Destination';

// Enumeration of possible tour statuses, based on the provided Python definition
export enum TourStatus {
  Created = 0,
  Planned = 1,
  InProgress = 3,
  Returning = 5,
  Stalled = 7,
  Completed = 8,
  Cancelled = 9,
  Failed = 10,
}

export interface TourJson {
  id: number;
  date: string;
  orders: any; // List of deliveries (called orders for historic reasons, need to refactor in the future)
  distance?: number;
  manual?: boolean;
  returnDuration?: number;
  tourDuration?: number;
  finishTime?: string;
  carrier?: Carrier;
  destination?: any;
  workforce: number;
  workload: number;
  drivers: Driver[];
  totalWeight: number;
  vehicleId?: number;
  note?: string;
  delivered: boolean;
  status: TourStatus;
  locked?: boolean;
}

export interface Tour extends TourJson {
  deliveries: number[];
  destination?: Destination;
  isOldTour: boolean;
  direct: boolean;
  isToday: boolean;
  color: string;
  textColor: string;
  isHomeTour: boolean;
  isKewlox: boolean;
  isBpost: boolean;
  isTourWithClientsPage: boolean;
  shouldPrintTourList: boolean;
  shouldPrintPackageSheetList: boolean;
  canManuallyUpdateDeliveriesStatus: boolean;
  isReseller: boolean;
  vehicleId?: number;
  title: string;
  note?: string;
  progression: number; // Number of delivery finish
  locked: boolean;
}

export const createTour = (data: TourJson): Tour => {
  const deliveries = data.orders
    .map(delivery => {
      return delivery.id;
    })
    .sort((a, b) => a.position - b.position);

  const tourDestination = data.destination
    ? createDestination(data.destination)
    : undefined;

  return {
    ...data,
    orders: [...data.orders].sort((a, b) => a.position - b.position),
    deliveries: deliveries,
    destination: tourDestination,
    isOldTour: isOldTour(data),
    direct: direct(data),
    isToday: isToday(parseISO(data.date)),
    color: color(data),
    textColor: textColor(data),
    isHomeTour: isHomeTour(tourDestination),
    isKewlox: isKewlox(data),
    isBpost: isBpost(data),
    isTourWithClientsPage: isTourWithClientsPage(data),
    shouldPrintTourList: shouldPrintTourList(data),
    shouldPrintPackageSheetList: shouldPrintPackageSheetList(data),
    canManuallyUpdateDeliveriesStatus: canManuallyUpdateDeliveriesStatus(
      data,
      deliveries,
    ),
    isReseller: isReseller(data),
    title: title(data),
    note: data.note,
    progression: getProgress(data.orders),
    locked: data.locked || false,
  };
};

const getProgress = (deliveries: Delivery[]) => {
  // Return the number of finish deliveries
  return deliveries.filter(delivery => delivery.delivered).length;
};

const isOldTour = (data: TourJson): boolean => {
  const tourDate = parseISO(data.date);
  return compareDesc(subDays(new Date(), 1), tourDate) === -1;
};

const direct = (data: TourJson): boolean => {
  return data.destination?.name === 'Direct';
};

const color = (data: TourJson): string => {
  if (data.destination) {
    if (data.destination.shop) {
      return data.destination.color;
    }
    if (data.carrier) {
      return data.carrier.color;
    }
    return data.destination.color;
  }
  if (data.carrier) {
    return data.carrier.color;
  }
  return '#a99090';
};

const textColor = (data: TourJson): string => {
  if (data.destination) {
    if (data.destination.shop) {
      return data.destination.textColor;
    }
    if (data.carrier) {
      return data.carrier.textColor;
    }
    return data.destination.textColor;
  }
  if (data.carrier) {
    return data.carrier.textColor;
  }
  return '#a99090';
};

const isHomeTour = (tourDestination?: Destination): boolean => {
  return tourDestination === undefined || tourDestination.home;
};

const isKewlox = (data: TourJson): boolean => {
  return (
    data.carrier?.name === 'KEWLOX' || data.carrier?.name === 'ALAIN VAN EYKEN'
  );
};

const isBpost = (data: TourJson): boolean => {
  return data.carrier?.name === 'BPOST';
};

const isTourWithClientsPage = (data: TourJson): boolean => {
  return (
    data.carrier?.name === 'BRENGER' ||
    data.carrier?.name === 'GARSOU-ANGENOT' ||
    data.carrier?.name === 'ZIEGLER'
  );
};

const shouldPrintTourList = (data: TourJson): boolean => {
  return (
    !direct(data) &&
    (isKewlox(data) ||
      data.carrier?.name === 'ALAIN VAN EYKEN' ||
      data.carrier?.name === 'VDB')
  );
};

const shouldPrintPackageSheetList = (data: TourJson): boolean => {
  return (
    (!shouldPrintTourList(data) || data.carrier?.name === 'VDB') &&
    !isBpost(data) &&
    !direct(data)
  );
};

const canManuallyUpdateDeliveriesStatus = (
  data: TourJson,
  deliveries: Delivery[],
): boolean => {
  return (
    deliveries.length > 0 &&
    (isReseller(data) || !(data.destination?.shop === true)) &&
    data.carrier?.name !== 'BPOST' &&
    !direct(data)
  );
};

const isReseller = (data: TourJson): boolean => {
  return data.destination?.name === 'Köln';
};

const title = (data: TourJson): string => {
  if (data.destination?.shop) {
    return data.destination.name;
  }
  return data.carrier?.name ?? 'NO CARRIER';
};

const ordersCustomer = (deliveries: Delivery[]): Delivery[] => {
  return deliveries.filter(o => o.order !== undefined);
};

const waypointsDeliveries = (orders: Delivery[]): Delivery[] => {
  return orders.filter(o => o.waypoint !== undefined);
};

const partOrdersList = (orders: Delivery[]): PartOrder[] => {
  return ordersCustomer(orders).map(delivery => delivery.order!);
};
